'use client'

import { alpha, IconButton, InputAdornment, TextField } from '@mui/material'
import { CloseIcon, SearchIcon } from 'icons'
import {
  useCallback,
  useRef,
  type KeyboardEventHandler,
  type RefObject,
} from 'react'

// A controlled search input: magnifying glass on the left, a clear (X) button
// on the right whenever there is text. Debouncing is intentionally left to the
// caller — this component has no async behavior of its own.
//
// A compact ~28px field matching the FilterDropdown trigger height, with the
// inner icons scaled down so they don't prop the field open.
//
// `highlightWhenActive` marks the field like an applied filter while it has
// text: primary border and a barely-there primary wash (the text stays
// readable through it). Leave it off for incidental searches, e.g. the
// search-within-a-dropdown input.
export function SearchField({
  value,
  onChange,
  placeholder,
  fullWidth = false,
  highlightWhenActive = false,
  onKeyDown,
  focusOnMount = false,
  inputRef,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  fullWidth?: boolean
  highlightWhenActive?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  focusOnMount?: boolean
  inputRef?: RefObject<HTMLInputElement | null>
}) {
  const ownInputRef = useRef<HTMLInputElement | null>(null)
  const resolvedInputRef = inputRef ?? ownInputRef

  // A ref callback rather than autoFocus (which the a11y lint rejects); stable so
  // React doesn't re-run it, and re-take focus, on every keystroke.
  const focusInput = useCallback(
    (input: HTMLInputElement | null) => {
      resolvedInputRef.current = input
      if (focusOnMount) input?.focus()
    },
    [focusOnMount, resolvedInputRef]
  )

  return (
    <TextField
      size="small"
      value={value}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      inputRef={focusInput}
      placeholder={placeholder}
      fullWidth={fullWidth}
      sx={[
        {
          // The root is pinned to the FilterDropdown trigger height so the
          // two control types always line up exactly; the flex root centers
          // the content. Pinning the input's height = lineHeight kills MUI's
          // 1.4375em input height (computed off the root font), which
          // off-centered the smaller text.
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            height: 28,
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '0.8125rem',
            color: 'text.secondary',
            py: 0,
            height: '16px',
            lineHeight: '16px',
            // MUI dims placeholders to ~0.42 opacity by default, which reads
            // too faint here — show it at the full secondary-text color.
            '&::placeholder': { color: 'text.secondary', opacity: 1 },
          },
          '& .MuiSvgIcon-root': { fontSize: 16 },
        },
        highlightWhenActive &&
          value !== '' &&
          ((theme) => ({
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          })),
      ]}
      slotProps={{
        htmlInput: { onKeyDown },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          // Always mounted, only hidden while empty: unmounting the X would
          // change the input's intrinsic width, so content-sized containers
          // (e.g. the FilterDropdown popover) would jump on the first
          // keystroke. visibility also drops it from hit-testing and the
          // accessibility tree while hidden. 'inherit', not 'visible', so a
          // hidden ancestor (TEACH's chrome behind SmallScreenGuard) keeps it hidden.
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{ visibility: value ? 'inherit' : 'hidden' }}
            >
              <IconButton
                size="small"
                aria-label="Clear search"
                sx={{ p: 0.25 }}
                onClick={() => {
                  onChange('')
                  // The X hides once the field is empty, which would drop focus to <body>.
                  resolvedInputRef.current?.focus()
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  )
}
